import {
  GetProfileMedicationsDtoDurationTypeEnum,
  GetProfileMedicationsDtoFormEnum,
  GetProfileMedicationsDtoFrequencyTypeEnum,
  GetProfileMedicationsDtoStatusEnum,
  GetProfileMedicationsDtoStopReasonEnum,
  GetProfileMedicationsDtoStrengthUnitEnum,
} from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';

export interface MedicationOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Every list ends with "Inne — wpisz ręcznie", which reveals the twin free-text
 * field. A vet who cannot express "ampułko-strzykawka" or "co drugi dzień" in
 * the form writes it in the notes, and the record stops being searchable.
 */
export const MEDICATION_FORM_OPTIONS: MedicationOption<GetProfileMedicationsDtoFormEnum>[] =
  [
    { value: 'TABLET', label: translateKey('medication.form.formTablet') },
    { value: 'CAPSULE', label: translateKey('medication.form.formCapsule') },
    { value: 'SACHET', label: translateKey('medication.form.formSachet') },
    {
      value: 'SUSPENSION',
      label: translateKey('medication.form.formSuspension'),
    },
    { value: 'SOLUTION', label: translateKey('medication.form.formSolution') },
    { value: 'DROPS', label: translateKey('medication.form.formDrops') },
    {
      value: 'INJECTION',
      label: translateKey('medication.form.formInjection'),
    },
    { value: 'OINTMENT', label: translateKey('medication.form.formOintment') },
    { value: 'SPOT_ON', label: translateKey('medication.form.formSpotOn') },
    { value: 'SPRAY', label: translateKey('medication.form.formSpray') },
    { value: 'POWDER', label: translateKey('medication.form.formPowder') },
    { value: 'PASTE', label: translateKey('medication.form.formPaste') },
    { value: 'OTHER', label: translateKey('medication.form.otherOption') },
  ];

export const MEDICATION_STRENGTH_UNIT_OPTIONS: MedicationOption<GetProfileMedicationsDtoStrengthUnitEnum>[] =
  [
    { value: 'MG', label: 'mg' },
    { value: 'G', label: 'g' },
    { value: 'MCG', label: 'µg' },
    { value: 'ML', label: 'ml' },
    { value: 'IU', label: translateKey('medication.form.unitIu') },
    { value: 'PERCENT', label: '%' },
    { value: 'NONE', label: translateKey('medication.form.unitNone') },
    { value: 'OTHER', label: translateKey('medication.form.otherOption') },
  ];

export const MEDICATION_FREQUENCY_OPTIONS: MedicationOption<GetProfileMedicationsDtoFrequencyTypeEnum>[] =
  [
    {
      value: 'TIMES_PER_DAY',
      label: translateKey('medication.form.frequencyTimesPerDay'),
    },
    {
      value: 'EVERY_N_HOURS',
      label: translateKey('medication.form.frequencyEveryNHours'),
    },
    {
      value: 'EVERY_N_DAYS',
      label: translateKey('medication.form.frequencyEveryNDays'),
    },
    {
      value: 'TIMES_PER_WEEK',
      label: translateKey('medication.form.frequencyTimesPerWeek'),
    },
    {
      value: 'TIMES_PER_MONTH',
      label: translateKey('medication.form.frequencyTimesPerMonth'),
    },
    {
      value: 'AS_NEEDED',
      label: translateKey('medication.form.frequencyAsNeeded'),
    },
    { value: 'OTHER', label: translateKey('medication.form.otherOption') },
  ];

export const MEDICATION_DURATION_OPTIONS: MedicationOption<GetProfileMedicationsDtoDurationTypeEnum>[] =
  [
    {
      value: 'UNTIL_FURTHER_NOTICE',
      label: translateKey('medication.form.durationUntilFurtherNotice'),
    },
    { value: 'DAYS', label: translateKey('medication.form.durationDays') },
    { value: 'WEEKS', label: translateKey('medication.form.durationWeeks') },
    {
      value: 'UNTIL_FOLLOW_UP',
      label: translateKey('medication.form.durationUntilFollowUp'),
    },
    {
      value: 'SINGLE_DOSE',
      label: translateKey('medication.form.durationSingleDose'),
    },
    { value: 'OTHER', label: translateKey('medication.form.otherOption') },
  ];

export const MEDICATION_STATUS_OPTIONS: MedicationOption<GetProfileMedicationsDtoStatusEnum>[] =
  [
    { value: 'SCHEDULED', label: translateKey('medication.status.scheduled') },
    { value: 'ACTIVE', label: translateKey('medication.status.active') },
    { value: 'PAUSED', label: translateKey('medication.status.paused') },
    { value: 'STOPPED', label: translateKey('medication.status.stopped') },
  ];

export const MEDICATION_STOP_REASON_OPTIONS: MedicationOption<GetProfileMedicationsDtoStopReasonEnum>[] =
  [
    {
      value: 'COURSE_COMPLETED',
      label: translateKey('medication.stopReason.courseCompleted'),
    },
    {
      value: 'INEFFECTIVE',
      label: translateKey('medication.stopReason.ineffective'),
    },
    {
      value: 'ADVERSE_REACTION',
      label: translateKey('medication.stopReason.adverseReaction'),
    },
    {
      value: 'OWNER_DECISION',
      label: translateKey('medication.stopReason.ownerDecision'),
    },
    { value: 'REPLACED', label: translateKey('medication.stopReason.replaced') },
    { value: 'OTHER', label: translateKey('medication.form.otherOption') },
  ];

/** Frequencies that need a number beside them ("2 × dziennie", "co 8 godzin"). */
export const FREQUENCIES_WITH_VALUE: GetProfileMedicationsDtoFrequencyTypeEnum[] =
  [
    'TIMES_PER_DAY',
    'EVERY_N_HOURS',
    'EVERY_N_DAYS',
    'TIMES_PER_WEEK',
    'TIMES_PER_MONTH',
  ];

/** Durations that need a number beside them ("7 dni", "3 tygodnie"). */
export const DURATIONS_WITH_VALUE: GetProfileMedicationsDtoDurationTypeEnum[] =
  ['DAYS', 'WEEKS'];

export const OTHER_OPTION = 'OTHER';
